import { useEffect } from 'react';
import { HERO_INTRO, easeInOutCubic } from '../utils/heroIntroMotion';
import { isHeroMobileViewport } from '../utils/heroMobile';

export function useHeroPanelIntro(heroRef, panelRefs, reducedMotion, onIntroReady) {
  useEffect(() => {
    const hero = heroRef.current;
    const panels = panelRefs
      .map(({ ref, introDelay = 0, initZ = 1.75 }) => ({
        el: ref.current,
        introDelay,
        initZ,
        introProgress: 0,
      }))
      .filter((p) => p.el);

    if (!hero || panels.length === 0) return undefined;

    let introReady = false;

    const markIntroReady = () => {
      if (introReady) return;
      introReady = true;
      hero.classList.add('is-intro-ready');
      onIntroReady?.(true);
    };

    const resetPanel = (el) => {
      el.style.transform = '';
      el.style.opacity = '1';
      el.style.filter = '';
      el.classList.remove('is-intro');
    };

    if (reducedMotion || isHeroMobileViewport()) {
      panels.forEach(({ el }) => resetPanel(el));
      markIntroReady();
      return () => {
        hero.classList.remove('is-intro-ready');
        onIntroReady?.(false);
      };
    }

    let frameCount = 0;
    let rafId = null;

    const measureOffsets = () => {
      const heroRect = hero.getBoundingClientRect();
      const cx = heroRect.width / 2;
      const cy = heroRect.height / 2;

      panels.forEach((panel) => {
        const rect = panel.el.getBoundingClientRect();
        const px = rect.left - heroRect.left + rect.width / 2;
        const py = rect.top - heroRect.top + rect.height / 2;
        panel.offsetX = cx - px;
        panel.offsetY = cy - py;
      });
    };

    const applyIntro = (panel, progress) => {
      const t = easeInOutCubic(progress);
      const scale = panel.initZ + (1 - panel.initZ) * t;
      const opacity = 0.12 + t * 0.88;
      const tx = panel.offsetX * (1 - t);
      const ty = panel.offsetY * (1 - t);

      panel.el.style.transform = `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`;
      panel.el.style.opacity = String(opacity);
      panel.el.style.filter = 'none';
    };

    const animate = () => {
      frameCount += 1;
      let pending = false;

      panels.forEach((panel) => {
        if (panel.introProgress >= HERO_INTRO.duration) {
          resetPanel(panel.el);
          return;
        }

        pending = true;

        if (frameCount <= panel.introDelay) {
          applyIntro(panel, 0);
          return;
        }

        panel.introProgress += 1;
        applyIntro(panel, panel.introProgress / HERO_INTRO.duration);
      });

      if (pending) {
        rafId = requestAnimationFrame(animate);
      } else {
        markIntroReady();
      }
    };

    panels.forEach(({ el }) => el.classList.add('is-intro'));
    measureOffsets();
    panels.forEach((panel) => applyIntro(panel, 0));
    rafId = requestAnimationFrame(animate);

    const onResize = () => {
      const anyIntro = panels.some((p) => p.introProgress < HERO_INTRO.duration);
      if (anyIntro) measureOffsets();
    };

    window.addEventListener('resize', onResize);
    const ro = new ResizeObserver(onResize);
    ro.observe(hero);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', onResize);
      ro.disconnect();
      panels.forEach(({ el }) => resetPanel(el));
      hero.classList.remove('is-intro-ready');
      onIntroReady?.(false);
    };
  }, [heroRef, panelRefs, reducedMotion, onIntroReady]);
}

export default useHeroPanelIntro;
